import { useState, useRef, useCallback, useEffect, MouseEvent } from "react";
import { IconButton, Popover } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { NavLinks } from "src/navigation/NavLinks";

export const MenuLinks = (): JSX.Element => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isMenuClicked, setIsMenuClicked] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setMenuAnchor(null);
    }, 100);
  }, [clearCloseTimeout]);

  const handleMouseEnter = (event: MouseEvent<HTMLElement>): void => {
    clearCloseTimeout();
    if (!isMenuClicked) {
      setMenuAnchor(event.currentTarget);
    }
  };

  const handleMouseLeave = (): void => {
    if (!isMenuClicked) {
      scheduleClose();
    }
  };

  const handleNavMenuClick = (event: MouseEvent<HTMLElement>): void => {
    clearCloseTimeout();
    if (menuAnchor && isMenuClicked) {
      setMenuAnchor(null);
      setIsMenuClicked(false);
    } else {
      setMenuAnchor(event.currentTarget);
      setIsMenuClicked(true);
    }
  };

  const handleMenuOptionClick = (): void => {
    clearCloseTimeout();
    setMenuAnchor(null);
    setIsMenuClicked(false);
  };

  return (
    <>
      <IconButton
        aria-controls="appbar-menu"
        aria-haspopup="true"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleNavMenuClick}
        disableRipple
        sx={{ "&:hover": { backgroundColor: "transparent" } }}
      >
        <MenuIcon
          sx={{
            marginRight: "10px",
            width: "36px",
          }}
        />
      </IconButton>
      <Popover
        sx={{ top: "20px", zIndex: "2500" }}
        id="appbar-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          clearCloseTimeout();
          setMenuAnchor(null);
          setIsMenuClicked(false);
        }}
        transitionDuration={200}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableRestoreFocus
        slotProps={{
          root: {
            sx: {
              pointerEvents: isMenuClicked ? "auto" : "none",
            },
          },
          paper: {
            sx: { pointerEvents: "auto" },
            onMouseEnter: clearCloseTimeout,
            onMouseLeave: handleMouseLeave,
          },
        }}
      >
        {/* Menu content */}
        <div onClick={handleMenuOptionClick}>
          <NavLinks />
        </div>
      </Popover>
    </>
  );
};
